import React, { memo } from 'react';
import { Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useWorkflowHelpers } from '../../hooks/useWorkflowHelpers';
import intl from '../i18n/ReactIntlWrapper';

interface WorkflowStatusChipProps {
  /**
   * The workflow state ID
   */
  stateId: number | null | undefined;
  
  /**
   * Optional custom color override
   */
  customColor?: string;
  
  /**
   * Chip variant
   */
  variant?: 'filled' | 'outlined';
  
  /**
   * Size of the chip
   */
  size?: 'small' | 'medium';
}

/**
 * A reusable component that displays a workflow status chip
 * using only the workflow state ID. It automatically resolves
 * the label key and color from the current workflow context.
 * 
 * @example
 * ```tsx
 * // Within a WorkflowProvider context
 * <WorkflowStatusChip stateId={employee.idWorkflowState} />
 * ```
 */
const WorkflowStatusChipComponent: React.FC<WorkflowStatusChipProps> = ({
  stateId,
  customColor,
  variant = 'outlined',
  size = 'small'
}) => {
  const theme = useTheme();
  const { getLabelKey, getColor, getDisplayName } = useWorkflowHelpers();

  // Get the label key for translation
  const labelKey = getLabelKey(stateId);
  
  // Get the workflow state color
  const stateColor = customColor || getColor(stateId);
  
  // Get display name as fallback if labelKey is not available
  const displayName = getDisplayName(stateId);

  // If no state is found, return null or a default chip
  if (!labelKey && !displayName) {
    return (
      <Chip
        variant={variant}
        size={size}
        label="Unknown Status"
        color="default"
      />
    );
  }

  // Use labelKey for translation if available, otherwise use displayName
  const label = labelKey ? intl.get(labelKey) : displayName;

  return (
    <Chip
      variant={variant}
      size={size}
      label={
        <Typography className="statusLabel" variant="body2">
          {label}
        </Typography>
      }
      sx={{
        backgroundColor: stateColor || theme.palette.grey[100],
        color: theme.palette.getContrastText(stateColor || theme.palette.grey[100]),
        borderColor: stateColor || theme.palette.grey[300],
        fontWeight: 500,
      }}
    />
  );
};

// Memoize the component to prevent unnecessary re-renders
export const WorkflowStatusChip = memo(WorkflowStatusChipComponent);
